import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';

import type { InferPropTypes } from '@agilemd/ui-utils';
import { useSpacing } from '@agilemd/ui-utils';

const propTypes = {
  /**
   * Top padding
   * `paddingTop * 8`
   */
  top: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /**
   * Bottom padding
   * `paddingBottom * 8`
   */
  bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /**
   * Left padding
   * `paddingLeft * 8`
   */
  left: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /**
   * Right padding
   * `paddingRight * 8`
   */
  right: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /**
   * Left and Right padding
   * `paddingLeft * 8`, `paddingRight * 8`
   */
  x: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /**
   * Top and Bottom padding
   * `paddingTop * 8`, `paddingBottom * 8`
   */
  y: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  /**
   * Any content to be padded about
   */
  children: PropTypes.any,
  /**
   * Whether the `Spacer` is `display:inline` else `display:block`
   * @default false AKA display:block
   */
  isInline: PropTypes.bool,
  /**
   * Whether the `Spacer` is `display:inline-block` else `display:block`
   * This will take precedence over `isInline`
   * @default false AKA display:block
   */
  isInlineBlock: PropTypes.bool
};

export type SpacerProps = InferPropTypes<typeof propTypes, never>;

/**
 * Used for quickly creating spacing between components,
 * without needing to breakout CSS for common configurations.
 * Internally uses inline styles to not have cascade issues,
 * no additional styles are allowed to be applied
 * use a CSS class name or container component if needed.
 */
const Spacer: React.FC<SpacerProps> = forwardRef<HTMLDivElement, SpacerProps>(
  (
    {
      top = false,
      bottom = false,
      right = false,
      left = false,
      x = false,
      y = false,
      children = null,
      isInline = false,
      isInlineBlock = false,
      ...props
    }: SpacerProps,
    ref
  ) => {
    const style = useSpacing(
      {
        top,
        bottom,
        right,
        left,
        x,
        y
      },
      {
        margin: false,
        style: {
          display: isInlineBlock ? 'inline-block' : isInline ? 'inline' : 'block'
        }
      }
    );

    return (
      <div className="aglmd-spacer" style={style} ref={ref} {...props}>
        {children}
      </div>
    );
  }
);

Spacer.propTypes = propTypes;
export default Spacer;
